import { useMutation } from '@apollo/client';
import type { UpdateControlledRiskOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateControlledRiskOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateControlledRiskOversightResultInput } from './useUpdateControlledRiskOversightResultTRPC';
import { useUpdateControlledRiskOversightResultTRPC } from './useUpdateControlledRiskOversightResultTRPC';

export const useUpdateControlledRiskOversightResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateControlledRiskOversightResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'risk_controlled_oversight_result');
        evictField(cache, 'risk_controlled_oversight_result_aggregate');
      },
    }
  );

  const trpcMutation = useUpdateControlledRiskOversightResultTRPC();

  const updateControlledRiskOversightResult = async (
    variables: UpdateControlledRiskOversightResultInput
  ): Promise<UpdateControlledRiskOversightResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateControlledRiskOversightResult(variables);
    }

    const result = await updateGraphQL({
      variables: {
        Id: variables.Id,
        Impact: variables.Impact,
        Likelihood: variables.Likelihood,
        Rating: variables.Rating,
        Rationale: variables.Rationale,
        TestDate: variables.TestDate,
        CustomAttributeData: variables.CustomAttributeData ?? null,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update controlled risk oversight result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateControlledRiskOversightResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateControlledRiskOversightResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
